// @ts-check
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, PushpinOutlined } from "@ant-design/icons";
import { List, Tag } from "antd";
import dayjs from "dayjs";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import beachImg from "~/assets/css/img/Beach.png";
import Button from "~/components/Shared/Button";
import { CourseIcon, FormIcon, PostIcon, TaskIcon } from "~/components/Shared/Icons";
import { BorderedPanel } from "~/components/Shared/PageParts";
import TeamListPopover from "~/components/Shared/TeamListPopover";
import TaskDetailModal from "~/components/Tasks/TaskDetailModal";
import useOrganizationId from "~/hooks/useOrganizationId";
import { dateFormatterFromString, dateFormatterNoTime } from "~/other/Helper";

export default function AssignmentsList({
    assignmentsType,
    assignments: assignmentsProp,
    onLoadMoreAssignments,
    loadingMoreAssignments,
    hasMoreAssignments,
    onMutation
}) {
    const orgId = useOrganizationId();
    const prevAssignmentsTypeRef = useRef(null);
    const listContainerRef = useRef(null);
    const [taskIdForModal, setTaskIdForModal] = useState(null);

    const assignments = useMemo(() => {
        return convertAssignments(orgId, assignmentsProp, setTaskIdForModal);
    }, [assignmentsProp, orgId]);

    useLayoutEffect(() => {
        if (prevAssignmentsTypeRef.current !== assignmentsType && listContainerRef.current) {
            listContainerRef.current.scrollTop = 0;
            prevAssignmentsTypeRef.current = assignmentsType;
        }
    }, [assignmentsType, assignments]);

    return (
        <>
            {taskIdForModal && <TaskDetailModal taskId={taskIdForModal} onClose={() => setTaskIdForModal(null)} onMutation={onMutation} />}
            <BorderedPanel
                style={{ height: "550px", overflow: "hidden", marginBottom: "1rem", padding: "1rem 0 0 2rem" }}
            >
                <h4
                    className="zero-blue pad-y-10"
                    style={{ height: "40px", margin: 0, borderBottom: "1px solid rgba(5, 5, 5, 0.06)" }}
                >
                    {assignmentsType === "open" ? "Open Assignments" : "Past Assignments 🔒"}
                </h4>
                {assignments.length === 0 ? (
                    <EmptyList />
                ) : (
                    <div ref={listContainerRef} style={{ height: "500px", overflow: "auto" }}>
                        <List
                            itemLayout="horizontal"
                            className="ant-list-vcenter-icons"
                            dataSource={assignments}
                            loadMore={
                                <LoadMoreButton
                                    onClick={onLoadMoreAssignments}
                                    loading={loadingMoreAssignments}
                                    hasMore={hasMoreAssignments}
                                />
                            }
                            renderItem={(item) => (
                                <List.Item key={item.id} id={item.id}>
                                    <List.Item.Meta
                                        avatar={item.avatar}
                                        title={item.title}
                                        description={<div className="zero-dark-grey">{item.description}</div>}
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                )}
            </BorderedPanel>
        </>
    );
}

function LoadMoreButton({ onClick, loading, hasMore }) {
    if (!hasMore) {
        return null;
    }
    return (
        <div className="text-center mar-y-10">
            <Button type="primary" onClick={onClick} loading={loading}>
                Load More
            </Button>
        </div>
    );
}

function EmptyList() {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: "2rem",
            }}
        >
            <img src={beachImg} alt="notification-img-2" style={{ width: "160px" }} />
            <p className="zero-dark-grey">Nothing new! We'll let you know if something comes up.</p>
        </div>
    );
}

/**
 * @param {string} orgId
 * @param {any[]} assignments
 * @param {(taskId: string) => void} setTaskIdForModal
 * @returns {{id: string, title: React.ReactNode, description: React.ReactNode, avatar: React.ReactNode}[]}
 */
function convertAssignments(orgId, assignments, setTaskIdForModal) {
    return assignments
        .map((assignment) => {
            if (assignment.type === "post") {
                const isOverDue =
                    assignment.due_date_timestamp &&
                    !assignment.completed &&
                    dayjs.unix(assignment.due_date_timestamp).isBefore(dayjs());
                return {
                    id: assignment.post_uuid,
                    title: (
                        <>
                            <Link
                                to={`/${orgId}/home/team/my_teams/feed/post/${assignment.post_uuid}`}
                                className="link-hover zero-blue"
                            >
                                {assignment.title}
                            </Link>
                            {isOverDue && (
                                <Tag color="red" className="text-normal mar-lft-5">
                                    Overdue
                                </Tag>
                            )}
                        </>
                    ),
                    description: getPostSubtitle(assignment),
                    avatar: <PostIcon size={24} />,
                };
            }

            if (assignment.type === "form" || assignment.type === "course") {
                const link = getFormOrCourseLink(orgId, assignment);
                return {
                    id: assignment.assignment_uuid,
                    title: link ? (
                        <Link to={link} className="link-hover zero-blue">
                            {assignment.title}
                        </Link>
                    ) : (
                        <div className="zero-blue">{assignment.title}</div>
                    ),
                    description: getFormOrCourseSubtitle(assignment),
                    avatar: assignment.type === "form" ? <FormIcon size={24} /> : <CourseIcon size={24} />,
                };
            }

            if (assignment.type === "task") {
                const isOverDue =
                    assignment.due_date_timestamp &&
                    !assignment.completed &&
                    dayjs.unix(assignment.due_date_timestamp).isBefore(dayjs());
                return {
                    id: assignment.task_uuid,
                    title: (
                        <>
                            <span
                                onClick={() => setTaskIdForModal(assignment.task_uuid)}
                                className="link-hover zero-blue"
                            >
                                {assignment.title}
                            </span>
                            {isOverDue && (
                                <Tag color="red" className="text-normal mar-lft-5">
                                    Overdue
                                </Tag>
                            )}
                        </>
                    ),
                    description: getTaskSubtitle(assignment),
                    avatar: <TaskIcon size={24} />,
                };
            }

            return null;
        })
        .filter(Boolean);
}

/**
 * @param {string} orgId
 * @param {any} assignment
 * @returns {string}
 */
function getFormOrCourseLink(orgId, assignment) {
    if (!assignment.team_uuids || assignment.team_uuids.length === 0) {
        return "";
    }
    let isOverDue = dayjs.unix(assignment.due_date_timestamp).isBefore(dayjs());

    if (assignment.type === "form") {
        if (assignment.completed) {
            return `/${orgId}/home/team/${assignment.team_uuids[0]}/forms/submission_view/${assignment.submission_uuid}`;
        } else if (!isOverDue) {
            if (assignment.submission_uuid) {
                return `/${orgId}/home/team/${assignment.team_uuids[0]}/forms/${assignment.form_uuid}/submission/${assignment.submission_uuid}?showPreviousDraftAssignmentAlert=1`;
            } else {
                return `/${orgId}/home/team/${assignment.team_uuids[0]}/forms/${assignment.form_uuid}/assignment/${assignment.assignment_uuid}/submit`;
            }
        }
    } else if (assignment.type === "course") {
        if (assignment.completed) {
            return `/${orgId}/home/team/${assignment.team_uuids[0]}/courses/submission_view/${assignment.submission_uuid}`;
        } else if (!isOverDue) {
            if (assignment.submission_uuid) {
                return `/${orgId}/home/team/${assignment.team_uuids[0]}/courses/${assignment.form_uuid}/submission/${assignment.submission_uuid}`;
            } else {
                return `/${orgId}/home/team/${assignment.team_uuids[0]}/courses/${assignment.form_uuid}/assignment/${assignment.assignment_uuid}/submit`;
            }
        }
    }

    return "";
}

function getPostSubtitle(assignment) {
    let subDescription;
    if (assignment.post_status === "closed" || assignment.completed) {
        subDescription = (
            <div>
                <CheckCircleOutlined className="mar-rgt-5" style={{ color: "#52c41a" }} />
                Closed {dateFormatterNoTime(assignment.completed_at)}
            </div>
        );
    } else if ((assignment.post_status === "open" || !assignment.completed) && !assignment.due_date) {
        subDescription = (
            <div>
                <PushpinOutlined className="mar-rgt-5" />
                Assigned {dateFormatterNoTime(assignment.assigned_at)}
            </div>
        );
    } else if ((assignment.post_status === "open" || !assignment.completed) && assignment.due_date) {
        subDescription = (
            <div>
                <ClockCircleOutlined className="mar-rgt-5" />
                Due {dateFormatterNoTime(assignment.due_date_timestamp)}
            </div>
        );
    }

    return (
        <div>
            <div>
                Shared in <TeamListPopover teamNames={assignment.team_names} />
            </div>
            {subDescription}
        </div>
    );
}

function getFormOrCourseSubtitle(assignment) {
    const isOverDue = dayjs.unix(assignment.due_date_timestamp).isBefore(dayjs());

    let subDescription;
    if (assignment.completed) {
        subDescription = (
            <div>
                <CheckCircleOutlined className="mar-rgt-5" style={{ color: "#52c41a" }} />
                Completed {dateFormatterNoTime(assignment.completed_at)}
            </div>
        );
    } else if (isOverDue && !assignment.excused) {
        subDescription = (
            <div>
                <CloseCircleOutlined className="mar-rgt-5" style={{ color: "#f56946" }} />
                Missed {dateFormatterFromString(assignment.due_date)}
            </div>
        );
    } else if (assignment.excused) {
        subDescription = (
            <div>
                <CloseCircleOutlined className="mar-rgt-5" style={{ color: "#f3b44b" }} />
                Excused {dateFormatterNoTime(assignment.excused_at)}
            </div>
        );
    } else {
        subDescription = (
            <div>
                <ClockCircleOutlined className="mar-rgt-5" />
                Due {dateFormatterNoTime(assignment.due_date_timestamp)}
            </div>
        );
    }

    return (
        <div>
            <div>{assignment.form_name}</div>
            {subDescription}
            {assignment.is_shared && <Tag className="tag-blue">Shared assignment</Tag>}
            {!assignment.completed && !isOverDue && assignment.submission_uuid && (
                <Tag style={{ color: "#505050" }}>Draft created</Tag>
            )}
        </div>
    );
}

function getTaskSubtitle(assignment) {
    const isCompleted = assignment.completed;

    if (isCompleted) {
        return (
            <div>
                <CheckCircleOutlined className="mar-rgt-5" style={{ color: "#52c41a" }} />
                Completed {dateFormatterNoTime(assignment.completed_at)}
            </div>
        );
    } else if (assignment.due_date_timestamp) {
        return (
            <div>
                <ClockCircleOutlined className="mar-rgt-5" />
                Due {dateFormatterNoTime(assignment.due_date_timestamp)}
            </div>
        );
    }

    return (
        <div>
            <PushpinOutlined className="mar-rgt-5" />
            Assigned {dateFormatterNoTime(assignment.assigned_at)}
        </div>
    );
}
